import React, { useState } from 'react';
import { Plus, Edit2, Trash2, ChevronUp, ChevronDown, Save, X } from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned', color: '#94A3B8' },
  { value: 'doing', label: 'In Progress', color: '#667eea' },
  { value: 'done', label: 'Completed', color: '#10B981' }
];

const MilestoneEditor = ({ milestones, clientId, onUpdate, backendUrl, adminKey }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date_iso: '',
    status: 'planned',
    note: '',
    key: ''
  });

  const handleAdd = () => {
    setEditingMilestone(null);
    setFormData({
      title: '',
      date_iso: '',
      status: 'planned',
      note: '',
      key: ''
    });
    setShowModal(true);
  };

  const handleEdit = (milestone) => {
    setEditingMilestone(milestone);
    setFormData({
      title: milestone.title || '',
      date_iso: milestone.date_iso ? milestone.date_iso.split('T')[0] : '',
      status: milestone.status || 'planned',
      note: milestone.note || '',
      key: milestone.key || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        order: editingMilestone ? editingMilestone.order : milestones.length
      };

      let response;
      if (editingMilestone) {
        // Update existing
        response = await fetch(`${backendUrl}/api/clients/${clientId}/milestones/${editingMilestone.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-key': adminKey
          },
          body: JSON.stringify(payload)
        });
      } else {
        // Create new
        response = await fetch(`${backendUrl}/api/clients/${clientId}/milestones`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-key': adminKey
          },
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      setShowModal(false);
      onUpdate();
    } catch (err) {
      console.error('Error saving milestone:', err);
      alert(`Failed to save milestone: ${err.message}`);
    }
  };

  const handleDelete = async (milestoneId) => {
    if (!confirm('Are you sure you want to delete this milestone?')) return;

    try {
      const response = await fetch(`${backendUrl}/api/clients/${clientId}/milestones/${milestoneId}`, {
        method: 'DELETE',
        headers: { 'x-admin-key': adminKey }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      onUpdate();
    } catch (err) {
      console.error('Error deleting milestone:', err);
      alert(`Failed to delete milestone: ${err.message}`);
    }
  };

  const handleMove = async (milestone, direction) => {
    const currentIndex = milestones.findIndex(m => m.id === milestone.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= milestones.length) return;

    try {
      // Update order for both milestones
      const response1 = await fetch(`${backendUrl}/api/clients/${clientId}/milestones/${milestone.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({ order: newIndex })
      });

      if (!response1.ok) {
        throw new Error(`Failed to update milestone position: ${response1.status}`);
      }

      const otherMilestone = milestones[newIndex];
      const response2 = await fetch(`${backendUrl}/api/clients/${clientId}/milestones/${otherMilestone.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({ order: currentIndex })
      });

      if (!response2.ok) {
        throw new Error(`Failed to update other milestone position: ${response2.status}`);
      }

      onUpdate();
    } catch (err) {
      console.error('Error moving milestone:', err);
      alert(`Failed to move milestone: ${err.message}`);
    }
  };

  const handleStatusChange = async (milestone, newStatus) => {
    try {
      const response = await fetch(`${backendUrl}/api/clients/${clientId}/milestones/${milestone.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to update status:', errorData);
        alert(`Failed to update status: ${errorData.detail || response.status}`);
        return;
      }

      // Success - refresh the milestone list
      onUpdate();
    } catch (err) {
      console.error('Error updating status:', err);
      alert(`Error updating status: ${err.message}`);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1E293B' }}>
          Project Timeline ({milestones.length} milestones)
        </h3>
        <button
          type="button"
          onClick={handleAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            background: '#667eea',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            fontFamily: 'inherit'
          }}
        >
          <Plus size={16} />
          Add Milestone
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {milestones.length === 0 ? (
          <div style={{
            padding: '32px',
            textAlign: 'center',
            color: '#94A3B8',
            fontSize: '14px',
            border: '2px dashed #E2E8F0',
            borderRadius: '12px'
          }}>
            No milestones yet. Click "Add Milestone" to create one.
          </div>
        ) : (
          milestones.map((milestone, index) => {
            const statusConfig = STATUS_OPTIONS.find(s => s.value === milestone.status) || STATUS_OPTIONS[0];
            
            return (
              <div
                key={milestone.id}
                style={{
                  padding: '14px',
                  border: '1px solid #E2E8F0',
                  borderRadius: '10px',
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                {/* Move buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <button
                    type="button"
                    onClick={() => handleMove(milestone, 'up')}
                    disabled={index === 0}
                    style={{
                      padding: '2px',
                      background: 'none',
                      border: 'none',
                      cursor: index === 0 ? 'not-allowed' : 'pointer',
                      opacity: index === 0 ? 0.3 : 1
                    }}
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMove(milestone, 'down')}
                    disabled={index === milestones.length - 1}
                    style={{
                      padding: '2px',
                      background: 'none',
                      border: 'none',
                      cursor: index === milestones.length - 1 ? 'not-allowed' : 'pointer',
                      opacity: index === milestones.length - 1 ? 0.3 : 1
                    }}
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#1E293B', marginBottom: '4px' }}>
                    {milestone.title}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748B' }}>
                    {milestone.date_iso ? formatDate(milestone.date_iso) : 'No date set'}
                  </div>
                  {milestone.note && (
                    <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px', fontStyle: 'italic' }}>
                      {milestone.note}
                    </div>
                  )}
                </div>

                {/* Status */}
                <select
                  value={milestone.status}
                  onChange={(e) => handleStatusChange(milestone, e.target.value)}
                  style={{
                    padding: '6px 10px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: statusConfig.color,
                    fontFamily: 'inherit'
                  }}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    type="button"
                    onClick={() => handleEdit(milestone)}
                    style={{
                      padding: '6px',
                      background: '#F1F5F9',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(milestone.id)}
                    style={{
                      padding: '6px',
                      background: '#FEF2F2',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={14} color="#EF4444" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1E293B' }}>
                {editingMilestone ? 'Edit Milestone' : 'Add Milestone'}
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={24} color="#64748B" />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Design Mockups"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Date
                </label>
                <input
                  type="date"
                  value={formData.date_iso}
                  onChange={(e) => setFormData({ ...formData, date_iso: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Note (optional)
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Add a description..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!formData.title}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '12px',
                    background: formData.title ? '#667eea' : '#CBD5E1',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: formData.title ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'inherit'
                  }}
                >
                  <Save size={16} />
                  {editingMilestone ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '12px 20px',
                    background: '#F1F5F9',
                    color: '#475569',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'inherit'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestoneEditor;
